import React, { useState, useEffect } from 'react';
import UserAvatar from './UserAvatar';
import Modal, { ConfirmModal } from '../composants/Modal.js';
import { useNavigate, useLocation } from 'react-router-dom';
import ShareURL from '../composants/ShareURL';
import Administrateur from '../phases/Administrateur';
import images from '../const/images';

const Header = ({ 
  user, 
  setUser, 
  joinSession, 
  setJoinSession, 
  creatingSession, 
  setCreatingSession, 
  isAdmin, 
  setIsAdmin, 
  currentPhase,
  goNext,
  closeSession,
  isEndSession = false
}) => {
  const [showUserSettings, setShowUserSettings] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [userName, setUserName] = useState('');
  const [inputError, setInputError] = useState(false);
  
  const [synthesisPreviouslyAvailable, setSynthesisPreviouslyAvailable] = useState(false);
  const [synthesisJustBecameAvailable, setSynthesisJustBecameAvailable] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  const goHome = () => {
    if (isEndSession) {
      navigate("/");
      return;
    }
    
    const isInSession = user.currentsession && user.currentsession !== "" && user.currentsession !== null;
    
    if (isInSession) {
      setShowLeaveConfirm(true);
    } else {
      // Si on n'est pas dans une session, on peut y aller directement
      // et s'assurer que l'état est propre
      setUser(prev => ({ ...prev, currentsession: null }));
      navigate("/");
    }
  };

  const confirmLeaveSession = () => {
  // 1. On ferme la modale immédiatement
  setShowLeaveConfirm(false);
  
  // 2. On navigue AVANT de modifier l'état pour éviter les renders intermédiaires
  navigate("/");
  
  // 3. On nettoie l'état après navigation (dans un setTimeout pour être sûr)
  setTimeout(() => {
    setUser(prev => ({ ...prev, currentsession: null }));
  }, 0);
};

  const resetFields = () => {
    setUserName(user.name);
    setInputError(false);
  };

  const handleSave = async () => {
    if (userName.length > 25) {
      setInputError(true);
      setTimeout(() => setInputError(false), 1500);
      return;
    }
    setUser({ ...user, name: userName });
    setShowUserSettings(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSave();
    }
  };

  const handleRoleChange = (newRole) => {
    setUser(prevUser => ({ ...prevUser, role: newRole }));
    setShowUserSettings(false);
  };

  useEffect(() => {
    if (showUserSettings) {
      resetFields();
    }
  }, [showUserSettings, user.name]);

  const getUserRoleText = () => {
    if (!user.currentsession) return 'Aucune session active';
    
    const roleNames = {
      0: 'Rédacteur',
      1: 'Évaluateur', 
      2: 'Spectateur'
    };
    
    return `${roleNames[user.role]} • Session ${user.currentsession}`;
  };

  const canShowSynthesis = !isEndSession && user.currentsession && currentPhase > 2;
  
  useEffect(() => {
    if (canShowSynthesis && !synthesisPreviouslyAvailable) {
      setSynthesisJustBecameAvailable(true);
      setTimeout(() => {
        setSynthesisJustBecameAvailable(false);
      }, 4000);
    }
    setSynthesisPreviouslyAvailable(canShowSynthesis);
  }, [canShowSynthesis, synthesisPreviouslyAvailable, currentPhase]);

  useEffect(() => {
    setSynthesisPreviouslyAvailable(false);
    setSynthesisJustBecameAvailable(false);
  }, [user.currentsession]);
  
  const canShowAdmin = !isEndSession && user.currentsession && isAdmin && currentPhase >= 0;
  
  const isHomePage = location.pathname === '/';
  const shouldShowHomeButton = !isHomePage;

  const roleOptions = [
    { id: 0, emoji: '🖊️', title: 'Rédacteur', description: 'Vous rédigez et vous évaluez' },
    { id: 1, emoji: '⭐', title: 'Évaluateur', description: 'Vous ne rédigez pas mais vous évaluez' },
    { id: 2, emoji: '👁️', title: 'Spectateur', description: 'Vous ne rédigez pas et n\'évaluez pas' }
  ];

  return (
    <>
      <header className="header">
        <div className="header-content">
          <div className="user-info" onClick={() => setShowUserSettings(true)}>
            <UserAvatar user={user} size={40} />
            <div className="user-details">
              <div className="user-name">{user.name || 'Utilisateur'}</div>
              <div className="user-role">{getUserRoleText()}</div>
            </div>
          </div>
          <div className="header-actions">
            {canShowAdmin && (
              <button 
                className="btn btn-ghost btn-sm"
                onClick={() => setShowAdminPanel(true)}
              >
                ⚙️ <span>Admin</span>
              </button>
            )}
            {canShowSynthesis && (
              <button 
                className={`btn btn-ghost btn-sm ${synthesisJustBecameAvailable ? 'synthesis-button-new' : ''}`}
                onClick={() => window.dispatchEvent(new CustomEvent('toggleSummaryPanel'))}
              >
                📋 <span>Synthèse</span>
              </button>
            )}
            {shouldShowHomeButton && (
              <button className="btn btn-ghost btn-sm" onClick={goHome}>
                🏠 <span>Accueil</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {showUserSettings && (
        <Modal
          title="⚙️ Paramètres utilisateur"
          onClose={() => {
            setShowUserSettings(false);
            resetFields();
          }}
        >
          <div className="user-settings-panel">
            {/* Section Profil */}
            <div className="settings-section">
              <div className="section-header">
                <span className="section-icon">👤</span>
                <h3 className="section-title">Profil</h3>
              </div>
              
              <div className="form-group">
                <label className="settings-label primary">Nom d'utilisateur</label>
                <div className="input-group">
                  <input
                    type="text"
                    className={`settings-input ${inputError ? 'error' : ''}`}
                    value={userName}
                    onChange={(e) => {
                      if (e.target.value.length <= 25) {
                        setUserName(e.target.value);
                      } else {
                        setInputError(true);
                        setTimeout(() => setInputError(false), 1500);
                      }
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="Entrez votre nom"
                  />
                  <button onClick={handleSave} className="btn btn-sm btn-primary">
                    Valider
                  </button>
                </div>
                {inputError && (
                  <p className="error-message">Le nom ne peut pas dépasser 25 caractères</p>
                )}
                <p className="field-hint">Ce nom sera visible par les autres participants</p>
              </div>
            </div>

            {/* Section Session */}
            {user.currentsession && (
              <div className="settings-section">
                <div className="section-header">
                  <span className="section-icon">🎭</span>
                  <h3 className="section-title">Session actuelle</h3>
                </div>
                
                <div className="session-info-card">
                  <div className="session-detail">
                    <span className="detail-label">Session</span>
                    <span className="detail-value">{user.currentsession}</span>
                  </div>
                  <div className="session-detail">
                    <span className="detail-label">Rôle actuel</span>
                    <span className="detail-value">
                      {user.role === 0 && '🖊️ Rédacteur'}
                      {user.role === 1 && '⭐ Évaluateur'}
                      {user.role === 2 && '👁️ Spectateur'}
                    </span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="settings-label primary">Changer de rôle</label>
                  <div className="role-tiles">
                    {roleOptions.map((role) => (
                      <div
                        key={role.id}
                        className={`role-tile ${user.role === role.id ? 'selected' : ''}`}
                        onClick={() => handleRoleChange(role.id)}
                      >
                        <div className="role-emoji">{role.emoji}</div>
                        <div className="role-title">{role.title}</div>
                        <div className="role-description">{role.description}</div>
                      </div>
                    ))}
                  </div>
                  <p className="field-hint">Vous pouvez changer de rôle à tout moment</p>
                </div>
              </div>
            )}

            {/* Section Partage */}
            <div className="settings-section">
              <div className="section-header">
                <span className="section-icon">📤</span>
                <h3 className="section-title">Partage</h3>
              </div>
              
              <div className="share-container">
                <ShareURL />
                <p className="field-hint">Partagez le lien de la session avec d'autres participants</p>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {showLeaveConfirm && (
        <ConfirmModal
          title="⚠️ Quitter la session"
          onClose={() => setShowLeaveConfirm(false)}
          onConfirm={confirmLeaveSession}
          confirmText="Quitter"
          cancelText="Rester"
        >
          <div className="text-center">
            <p className="mb-3">
              Êtes-vous sûr de vouloir quitter la session en cours ?
            </p>
            <p className="text-sm text-warning mt-3">
              💡 Vous pourrez toujours revenir en rejoignant la session <strong>{user.currentsession}</strong> plus tard
            </p>
          </div>
        </ConfirmModal>
      )}

      {showAdminPanel && (
        <Modal 
          title="⚙️ Actions administrateur" 
          onClose={() => setShowAdminPanel(false)}
          size="large"
        >
          <Administrateur
            user={user}
            setUser={setUser}
            phase={currentPhase}
            goNext={goNext}
            closeSession={closeSession}
            redaction={currentPhase % 2 !== 0}
            setShowInfoAdmin={setShowAdminPanel}
          />
        </Modal>
      )}
    </>
  );
};

export default Header;